import React, { useEffect, useState } from 'react';
import FormNavigation from 'components/FormNavigation';
import { FlowComponentType } from 'routes/types';
import { useDispatch, useSelector } from 'react-redux';
import { YourIncomeVariable } from 'enums/LoanFormVariables';
import FormContainer from 'components/LoanForm/FormContainer';
import CheckboxSmall from 'components/Checkbox/CheckboxSmall';
import Button from 'components/Button';
import MonthYearPicker from 'components/MonthYearPicker';
import { useForm } from 'react-hook-form';
import { getMessageForRequiredFields } from 'utils/errors';
import { formatMonthYear, getYearsFromNow } from 'utils/dateUtils';
import { getHighestTierCredential } from 'utils/credentialUtils';
import Input from 'components/Input';
import { setYourIncomeData } from 'handlers/yourIncome';
import { PreQualificationData, setPartnerName } from 'handlers/preQualificationData';
import { YourEmployerResult } from 'enums/FlowNextResults';
import { getPartnerFromEmployerName, PARTNER_DATA, PartnerType } from 'enums/PartnerName';
import { getPreQualificationData } from 'selectors/preQualificationData';

import { validateEmployerProvidedIncome } from 'thunks';
import { RootState } from 'handlers';
import { getYourIncome } from 'selectors/yourIncome';

import styles from './YourEmployer.module.scss';

export enum YourEmployerInputLabel {
  EmployerName = 'Primary employer name',
  NotCurrentlyEmployed = 'Not currently employed',
  CredentialAcquisitionDate = 'Date you first got your [user-credentials] credential',
}

const replaceCredentialPlaceholder = (label: string, credential: string) =>
  label.replace('[user-credentials]', credential);

const YourEmployer = ({ navigationInfo, handleNext }: FlowComponentType): JSX.Element => {
  const dispatch = useDispatch();
  const {
    yourBirthDate: { birth_date: birthDate },
    yourContact: yourContactData,
    yourName: yourNameData,
    yourIncome: yourIncomeData,
    yourAddress: yourDetailsData,
    employers: {
      validateEmployerProvidedIncome: { incomeRequired, isLoading: isIncomeVerificationLoading },
    },
  } = useSelector((state: RootState) => state);
  const [shouldRedirectAfterIncomeVerification, setShouldRedirectAfterIncomeVerification] = useState(false);
  const { partnerId: partnerName } = useSelector(getPreQualificationData);
  const defaultValues = useSelector(getYourIncome);
  const { credentials } = yourNameData;
  const shouldShowCredentialAcquisitionDate = !!credentials;
  const minDate = getYearsFromNow(-50);
  const maxDate = getYearsFromNow(0);
  const highestTierCredential = credentials ? getHighestTierCredential(credentials) : '';
  const {
    formState: { errors, isValid },
    register,
    watch,
    trigger,
    setValue,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      ...defaultValues,
      ...(partnerName &&
        PARTNER_DATA[partnerName].type === PartnerType.Employer && {
          [YourIncomeVariable.EmployerName]: PARTNER_DATA[partnerName].fullName,
        }),
    },
  });

  const watcher = watch();

  useEffect(() => {
    if (shouldRedirectAfterIncomeVerification && !isIncomeVerificationLoading) {
      setShouldRedirectAfterIncomeVerification(false);
      if (incomeRequired) {
        handleNext(YourEmployerResult.IncomeRequired);
      } else {
        handleNext();
      }
    }
  }, [incomeRequired, isIncomeVerificationLoading, shouldRedirectAfterIncomeVerification, handleNext]);

  const validateEmploymentFields = (errorMessage: string) => (value: any) => {
    const isNotEmployed = watch()[YourIncomeVariable.NotEmployed];

    return isNotEmployed || (value !== null && value !== undefined && value !== '') ? true : errorMessage;
  };

  const validateCredentialAcquisitionDateField = (errorMessage: string) => (value: string) => {
    if (!value) return errorMessage;

    const parsedDate = new Date(value);

    if (parsedDate < minDate) return `Date must be after ${formatMonthYear(minDate)}`;
    if (parsedDate > maxDate) return `Date must be before or equal to ${formatMonthYear(maxDate)}`;
  };

  useEffect(() => {
    register(YourIncomeVariable.EmployerName, {
      validate: validateEmploymentFields(getMessageForRequiredFields(YourEmployerInputLabel.EmployerName)),
    });
    register(YourIncomeVariable.NotEmployed, {
      validate: validateEmploymentFields(getMessageForRequiredFields(YourEmployerInputLabel.NotCurrentlyEmployed)),
    });
    if (shouldShowCredentialAcquisitionDate) {
      register(YourIncomeVariable.CredentialAcquisitionDate, {
        validate: validateCredentialAcquisitionDateField(
          getMessageForRequiredFields(
            replaceCredentialPlaceholder(YourEmployerInputLabel.CredentialAcquisitionDate, highestTierCredential),
          ),
        ),
      });
    }
  }, [register, shouldShowCredentialAcquisitionDate, highestTierCredential]);

  const handleContinue = async () => {
    checkForPartner(watcher[YourIncomeVariable.EmployerName] || '');

    dispatch(
      setYourIncomeData({
        ...yourIncomeData,
        [YourIncomeVariable.EmployerName]: watcher[YourIncomeVariable.EmployerName],
        [YourIncomeVariable.NotEmployed]: watcher[YourIncomeVariable.NotEmployed],
        ...(shouldShowCredentialAcquisitionDate && {
          [YourIncomeVariable.CredentialAcquisitionDate]: watcher[YourIncomeVariable.CredentialAcquisitionDate],
        }),
      }),
    );

    if (watcher[YourIncomeVariable.NotEmployed]) return handleNext(YourEmployerResult.IncomeRequired);

    setShouldRedirectAfterIncomeVerification(true);
    dispatch(
      validateEmployerProvidedIncome({
        employerName: watcher[YourIncomeVariable.EmployerName] || '',
        borrower: {
          email: yourContactData.email,
          phoneNumber: yourContactData.phone_number,
          firstName: yourNameData.first_name,
          lastName: yourNameData.last_name,
          city: yourDetailsData.city,
          state: yourDetailsData.argyle_state,
          dateOfBirth: birthDate,
        },
      }),
    );
  };

  const checkForPartner = (employerName: string) => {
    let currentPartnerIsEmployer = false;

    if (partnerName) {
      currentPartnerIsEmployer = PARTNER_DATA[partnerName].type === PartnerType.Employer;
      if (!currentPartnerIsEmployer) {
        return;
      }
    }

    const partnerId = getPartnerFromEmployerName(employerName);

    if (partnerId) {
      const preQualificationData: PreQualificationData = {
        partnerId,
      };
      dispatch(setPartnerName(preQualificationData));
    } else if (currentPartnerIsEmployer) {
      // Clean up partner data if employer name was changed
      dispatch(setPartnerName({}));
    }
  };

  const handleNotEmployed = (checked: boolean) => {
    setValue(YourIncomeVariable.NotEmployed, checked);
    setValue(YourIncomeVariable.EmployerName, '');
    trigger(YourIncomeVariable.EmployerName);
  };

  return (
    <>
      <FormNavigation {...navigationInfo} />
      <FormContainer title="Who is your primary employer?" subtitle="We need this to pull your data.">
        <div className={styles.mainContainer}>
          <Input
            label={YourEmployerInputLabel.EmployerName}
            placeholder={YourEmployerInputLabel.EmployerName}
            errorMessage={errors[YourIncomeVariable.EmployerName]?.message}
            name={YourEmployerInputLabel.EmployerName}
            onChange={(event: React.FocusEvent<HTMLInputElement>) => {
              setValue(YourIncomeVariable.EmployerName, event.target.value);
              trigger(YourIncomeVariable.EmployerName);
            }}
            value={watcher[YourIncomeVariable.EmployerName]}
            onKeyUp={(e) => e.key === 'Enter' && isValid && handleContinue()}
            disabled={watcher[YourIncomeVariable.NotEmployed]}
            autoFocus
          />
          <CheckboxSmall
            label={YourEmployerInputLabel.NotCurrentlyEmployed}
            name={YourIncomeVariable.NotEmployed}
            onChange={(event) => handleNotEmployed(event.target.checked)}
            checked={watcher[YourIncomeVariable.NotEmployed]}
            checkboxLabelClassName={styles.checkboxLabel}
          />
          {shouldShowCredentialAcquisitionDate && (
            <MonthYearPicker
              minDate={minDate}
              maxDate={maxDate}
              name={YourIncomeVariable.CredentialAcquisitionDate}
              label={replaceCredentialPlaceholder(
                YourEmployerInputLabel.CredentialAcquisitionDate,
                highestTierCredential,
              )}
              value={watcher[YourIncomeVariable.CredentialAcquisitionDate] ?? undefined}
              errorMessage={errors[YourIncomeVariable.CredentialAcquisitionDate]?.message}
              onChange={(date: string | undefined) => {
                setValue(YourIncomeVariable.CredentialAcquisitionDate, date);
                trigger(YourIncomeVariable.CredentialAcquisitionDate);
              }}
            />
          )}
        </div>

        <Button
          onClick={handleContinue}
          disabled={!isValid || isIncomeVerificationLoading}
          isLoading={isIncomeVerificationLoading}
        >
          Next
        </Button>
      </FormContainer>
    </>
  );
};

export default YourEmployer;
